<script setup lang="ts">
const { t } = useI18n()

const { proxy: _proxyMetaPixel, load: loadMetaPixel, onLoaded: _onLoadedMetaPixel } = useMetaPixel()

const metaPixelEnabled = useCookie(
  'meta_pixel_enabled',
  {
    default: () => false,
    maxAge: 360 * 24 * 60 * 60,
  },
)

onMounted(() => {
  if (metaPixelEnabled.value) {
    // TODO loadMetaPixel()
  }

  loadMetaPixel()
})

const cookieConsentGiven = useCookie(
  'cookie_consent_given',
  {
    default: () => false,
    maxAge: 270 * 24 * 60 * 60,
  },
)

const detailsVisible = ref(false)
const metaPixelSelected = ref(false)

watch(cookieConsentGiven, () => {
  detailsVisible.value = false
  metaPixelSelected.value = false
})

function enableMetaPixel() {
  if (!metaPixelEnabled.value) {
    metaPixelEnabled.value = true
    // TODO loadMetaPixel()
    // proxyMetaPixel.fbq('consent', 'grant')
  }
}

function disableMetaPixel() {
  if (metaPixelEnabled.value) {
    metaPixelEnabled.value = false
    // TODO cleanup
    // unload meta pixel so it can be loaded again?
    // remove _fbp cookie, etc.
    // make sure third-party-cookies are removed, proxyMetaPixel.fbq('consent', 'revoke')
  }
}

function acceptAllCookies() {
  metaPixelSelected.value = true
  saveSettings()
}

function rejectOptionalCookies() {
  metaPixelSelected.value = false
  saveSettings()
}

function saveSettings() {
  if (metaPixelSelected.value) {
    enableMetaPixel()
  }
  else {
    disableMetaPixel()
  }

  cookieConsentGiven.value = true
}

const essentialCookies = ref([
  { name: 'i18n_redirected', provider: 'Introfy GmbH', maxage: t('1 Jahr'), description: t('Spracheinstellung') },
  { name: 'country_setting', provider: 'Introfy GmbH', maxage: t('360 Tage'), description: t('Ländereinstellung') },
  { name: 'candidate_tkn', provider: 'Introfy GmbH', maxage: t('Sitzung'), description: t('Kandidaten-Sitzung') },
  { name: 'internal_user_tkn', provider: 'Introfy GmbH', maxage: t('Sitzung'), description: t('Administratoren-Sitzung') },
  { name: 'cookie_consent_given', provider: 'Introfy GmbH', maxage: t('270 Tage'), description: t('Cookie-Consent-Einstellung') },
  { name: 'meta_pixel_enabled', provider: 'Introfy GmbH', maxage: t('360 Tage'), description: t('Meta-Pixel-Einstellung') },
])

const targetingCookies = ref([
  { name: '_fbp', provider: 'Meta Platforms, Inc.', maxage: t('3 Monate'), description: t('Das Meta Pixel erfasst Nutzerdaten zur Analyse, Optimierung von Werbeanzeigen und Erstellung personalisierter Zielgruppen auf Facebook und Instagram.') },
  { name: '_fbc', provider: 'Meta Platforms, Inc.', maxage: t('3 Monate'), description: t('Das Meta Pixel erfasst Nutzerdaten zur Analyse, Optimierung von Werbeanzeigen und Erstellung personalisierter Zielgruppen auf Facebook und Instagram.') },
])
</script>

<template>
  <Dialog
    class="dialog-footer-no-flex"
    :visible="!cookieConsentGiven"
    modal
    :draggable="false"
    :header="t('Diese Webseite verwendet Cookies.')"
    :style="{ width: '50vw' }"
    :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
    :closable="false"
  >
    <div v-if="!detailsVisible">
      <div>
        <p>{{ t("Unsere Webseite verwendet Cookies. Für bestimmte Cookies ist eine Einwilligung über diesen Consent-Manager erforderlich. Sie können Ihre Einwilligung zur Verwendung von Cookies auf unserer Website jederzeit in diesem Consent-Manager ändern. Näheres zu den Cookies findet sich unter „Details anzeigen“ oder auch in unseren Datenschutzhinweisen.") }}</p>
        <p class="mt-2">
          {{ t("Die Datenschutzhinweise finden Sie unter dem Link „Datenschutz“ im der Fußzeile dieser Website.") }}
        </p>
      </div>
    </div>
    <div v-if="detailsVisible">
      <div>
        <p class="mb-4">
          {{ t("Cookies sind kleine Textdateien, die auf Ihrem Computer abgelegt werden, wenn Sie bestimmte Websites besuchen. Websites verwenden Cookies, um Benutzern das Navigieren auf einer Website zu erleichtern und das Ausführen bestimmter Funktionen zu ermöglichen. Cookies, die für den ordnungsgemäßen Betrieb der Website erforderlich sind, dürfen ohne Ihre Einwilligung gesetzt werden. Allen anderen Cookies muss erst zugestimmt werden, bevor sie im Browser gesetzt werden können. Sie können Ihre Einwilligung zur Verwendung von Cookies auf unserer Website jederzeit in der Datenschutzerklärung ändern.") }}
        </p>
        <CustomCard class="mb-2">
          <div class="text-lg font-medium mb-2 flex justify-between">
            <span>{{ t('Nötige Cookies') }}</span>
            <ToggleSwitch
              :model-value="true"
              disabled
            />
          </div>
          <p class="mb-2">
            {{ t("Nötige Cookies ermöglichen wesentliche Kernfunktionen der Website wie die Benutzeranmeldung und die Kontoverwaltung. Ohne die nötigen Cookies kann die Website nicht ordnungsgemäß verwendet werden.") }}
          </p>
          <DataTable
            :value="essentialCookies"
            show-gridlines
          >
            <Column
              field="name"
              :header="t('Name')"
            />
            <Column
              field="provider"
              :header="t('Empfänger')"
            />
            <Column
              field="maxage"
              :header="t('Gültigkeit')"
            />
            <Column
              field="description"
              :header="t('Beschreibung')"
            />
          </DataTable>
        </CustomCard>
        <CustomCard>
          <div class="text-lg font-medium mb-2 flex justify-between">
            <span>{{ t('Targeting') }}</span>
            <ToggleSwitch v-model="metaPixelSelected" />
          </div>
          <p class="mb-2">
            {{ t("Targeting-Cookies werden verwendet, um Besucher über verschiedene Websites hinweg zu identifizieren, z. B. durch Content-Partner oder Banner-Netzwerke. Diese Cookies können von Unternehmen genutzt werden, um ein Profil der Interessen von Besuchern zu erstellen oder relevante Werbung auf anderen Websites anzuzeigen.") }}
          </p>
          <DataTable
            :value="targetingCookies"
            show-gridlines
          >
            <Column
              field="name"
              :header="t('Name')"
            />
            <Column
              field="provider"
              :header="t('Empfänger')"
            />
            <Column
              field="maxage"
              :header="t('Gültigkeit')"
            />
            <Column
              field="description"
              :header="t('Beschreibung')"
            />
          </DataTable>
        </CustomCard>
      </div>
    </div>
    <template
      #footer
    >
      <div
        v-if="!detailsVisible"
        class="mt-4 flex lg:flex-row flex-col gap-2"
      >
        <Button
          class="w-full"
          type="button"
          :label="t('Details anzeigen')"
          severity="secondary"
          @click="detailsVisible = true"
        />
        <Button
          class="w-full"
          type="button"
          :label="t('Optionale Cookies ablehnen')"
          severity="secondary"
          @click="rejectOptionalCookies"
        />
        <Button
          class="w-full"
          type="button"
          :label="t('Alle Cookies erlauben')"
          @click="acceptAllCookies"
        />
      </div>
      <div
        v-if="detailsVisible"
        class="mt-4 flex lg:flex-row flex-col gap-2"
      >
        <Button
          class="w-full"
          type="button"
          :label="t('Optionale Cookies ablehnen')"
          severity="secondary"
          @click="rejectOptionalCookies"
        />
        <Button
          class="w-full"
          type="button"
          :label="t('Einstellungen speichern')"
          severity="secondary"
          @click="saveSettings"
        />
        <Button
          class="w-full"
          type="button"
          :label="t('Alle Cookies erlauben')"
          @click="acceptAllCookies"
        />
      </div>
    </template>
  </Dialog>
</template>
